import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { QcIncidentNotesThread } from "@/components/qc/QcIncidentNotesThread";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";
import { QualityOwnerSelect } from "@/pages/quality/QualityOwnerSelect";
import { TierReviewProgress } from "@/pages/quality/QualityTierReviewQueuePage";
import { qualityEventLabel, qualityEventStatusLabel, qualityNcrLabel } from "@/pages/quality/qualityEventNcrApi";

type ReviewPayload = {
  incidentId: number;
  issue: string | null;
  issueNotes: string | null;
  issueDate: string | null;
  tier1Issue: string | null;
  incidentType: string | null;
  subtype: string | null;
  serial: string | null;
  locationName: string | null;
  event: { id: number; source: string | null; summary: string | null; status: "open" | "closed" } | null;
  stage: string;
  tier1: {
    reviewId: number | null;
    ownerUserId: string | null;
    ownerDisplayName: string | null;
    completed: boolean;
    outcome: string | null;
    steps: { stepId: number; prompt: string; resultText: string }[];
  };
  tier2: {
    reviewId: number | null;
    completed: boolean;
    outcome: string | null;
    loanSentAt: string | null;
    loanReturnedAt: string | null;
    log: { id: number; tried: string; result: string; createdAt: string }[];
  };
};

export function QualityTierReviewPage({ tier }: { tier: 1 | 2 }) {
  const params = useParams();
  const incidentId = params.incidentId ?? "";
  const queryClient = useQueryClient();
  const review = useQuery({
    queryKey: ["quality", "tier-review", incidentId],
    queryFn: () => apiFetch<ReviewPayload>(`/api/quality/tier-reviews/${incidentId}`),
    enabled: incidentId !== "",
  });
  const start = useMutation({
    mutationFn: () =>
      apiFetch(`/api/quality/tier-reviews/${incidentId}/start`, { method: "POST", body: JSON.stringify({ tier }) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["quality", "tier-review", incidentId] }),
  });
  const started = useRef(false);
  useEffect(() => {
    if (started.current || review.data == null) return;
    const needsTier1 = tier === 1 && review.data.tier1.reviewId == null && !review.data.tier1.completed;
    const needsTier2 = tier === 2 && review.data.tier2.reviewId == null && review.data.stage === "tier_2";
    if (!needsTier1 && !needsTier2) return;
    started.current = true;
    start.mutate();
  }, [review.data, start, tier]);

  const data = review.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <PageTitleWithHelp
          breadcrumbLabel={qualityNcrLabel(Number(incidentId))}
          hoverHint="Record the review for this NCR. A final outcome resolves the NCR. Escalate sends it to tier 2."
        >
          {qualityNcrLabel(Number(incidentId))}
        </PageTitleWithHelp>
        {data == null ? null : <TierReviewProgress stage={data.stage} />}
      </div>
      {data == null ? null : <NcrEventCard data={data} />}
      {tier === 1 && data != null ? <Tier1Form incidentId={incidentId} data={data} /> : null}
      {tier === 2 && data != null ? <Tier2Form incidentId={incidentId} data={data} /> : null}
    </div>
  );
}

function NcrEventCard({ data }: { data: ReviewPayload }) {
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="The NCR is this unit. The event is the shared reason linked to it.">
          NCR and event
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody>
        <dl className="grid gap-3 sm:grid-cols-3">
          <Field label="Unit" value={unitKindLabel(data.incidentType, data.subtype)} />
          <Field label="Serial" value={data.serial ?? EM_DASH} />
          <Field label="Location" value={data.locationName ?? EM_DASH} />
          <Field label="Opened" value={data.issueDate ?? EM_DASH} />
          <Field label="Tier 1 issue" value={data.tier1Issue ?? EM_DASH} />
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Issue</dt>
            <dd className="whitespace-pre-wrap text-sm text-slate-900">{data.issue ?? EM_DASH}</dd>
          </div>
          <div className="sm:col-span-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Issue notes</dt>
            <dd className="whitespace-pre-wrap text-sm text-slate-900">{data.issueNotes ?? EM_DASH}</dd>
          </div>
        </dl>
        <div className="mt-4 border-t border-slate-200 pt-4">
          {data.event == null ? (
            <p className="text-sm text-slate-600">This NCR is not linked to a quality event.</p>
          ) : (
            <dl className="grid gap-3 sm:grid-cols-3">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Event</dt>
                <dd className="text-sm">
                  <Link className="text-sky-700 hover:underline" to={`/quality/events/${data.event.id}`}>
                    {qualityEventLabel(data.event.id)}
                  </Link>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Event status</dt>
                <dd className="text-sm">
                  <Badge tone={data.event.status === "closed" ? "neutral" : "info"}>
                    {qualityEventStatusLabel(data.event.status)}
                  </Badge>
                </dd>
              </div>
              <Field label="Source" value={data.event.source ?? EM_DASH} />
              <div className="sm:col-span-3">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Summary</dt>
                <dd className="whitespace-pre-wrap text-sm text-slate-900">{data.event.summary ?? EM_DASH}</dd>
              </div>
            </dl>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function unitKindLabel(incidentType: string | null, subtype: string | null): string {
  if (incidentType === "device") return "Device";
  if (incidentType === "pcba") return "PCBA";
  if (incidentType === "clamp") {
    const detail = subtype?.trim() ?? "";
    return detail === "" ? "Clamp" : `Clamp (${detail})`;
  }
  return EM_DASH;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function Tier1Form({ incidentId, data }: { incidentId: string; data: ReviewPayload }) {
  const queryClient = useQueryClient();
  const writesAllowed = useHubModuleWrite("quality");
  const meQuery = useIntegratorMe();
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [resolveOpen, setResolveOpen] = useState(false);
  const saveTimer = useRef<number | null>(null);
  const steps = data.tier1.steps;
  const save = useMutation({
    mutationFn: (step: { stepId: number; resultText: string }) =>
      apiFetch(`/api/quality/tier-reviews/${incidentId}/steps`, {
        method: "PUT",
        body: JSON.stringify({ steps: [step] }),
      }),
  });
  const complete = useMutation({
    mutationFn: (outcome: string) =>
      apiFetch(`/api/quality/tier-reviews/${incidentId}/complete`, {
        method: "POST",
        body: JSON.stringify({ tier: 1, outcome }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["quality", "tier-review", incidentId] }),
  });
  const assignOwner = useMutation({
    mutationFn: (ownerUserId: string) =>
      apiFetch(`/api/quality/tier-reviews/${incidentId}/owner`, {
        method: "PUT",
        body: JSON.stringify({ ownerUserId }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["quality", "tier-review", incidentId] }),
  });
  function scheduleSave(stepId: number, resultText: string): void {
    if (saveTimer.current != null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      save.mutate({ stepId, resultText });
    }, 400);
  }
  if (data.tier1.completed) {
    return <p className="text-sm text-slate-700">Tier 1 is complete ({data.tier1.outcome}).</p>;
  }
  const currentUserId = meQuery.data?.user.id ?? null;
  const isSelf = currentUserId != null && data.tier1.ownerUserId === currentUserId;
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Steps for this tier 1 issue. Answers save as you type.">
          Tier 1 script
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-64">
            <label htmlFor="tier-1-reviewer" className="text-xs font-medium uppercase tracking-wide text-slate-500">Tier 1 reviewer</label>
            <QualityOwnerSelect
              id="tier-1-reviewer"
              ariaLabel="Tier 1 reviewer"
              value={data.tier1.ownerUserId}
              currentLabel={data.tier1.ownerDisplayName}
              disabled={!writesAllowed || assignOwner.isPending}
              onChange={(ownerUserId) => {
                if (ownerUserId != null) assignOwner.mutate(ownerUserId);
              }}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="mb-0.5"
            disabled={!writesAllowed || isSelf || currentUserId == null || assignOwner.isPending}
            onClick={() => {
              if (currentUserId != null) assignOwner.mutate(currentUserId);
            }}
          >
            Take over
          </Button>
        </div>
        {steps.length === 0 ? <p className="text-sm text-slate-600">No defined Tier 1 scripts for this issue.</p> : null}
        {steps.map((step) => (
          <label key={step.stepId} className="block text-sm text-slate-800">
            {step.prompt}
            <textarea
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
              rows={2}
              value={answers[step.stepId] ?? step.resultText}
              onChange={(event) => {
                const resultText = event.target.value;
                setAnswers((current) => ({ ...current, [step.stepId]: resultText }));
                scheduleSave(step.stepId, resultText);
              }}
            />
          </label>
        ))}
        <QcIncidentNotesThread incidentId={Number(incidentId)} writesAllowed={writesAllowed} />
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Button type="button" disabled={complete.isPending} onClick={() => setResolveOpen((open) => !open)}>
              Mark resolved
            </Button>
            {resolveOpen ? (
              <div className="absolute left-0 z-10 mt-1 w-44 rounded-md border border-slate-200 bg-white p-1 shadow-md">
                <ResolveChoice label="Repaired" onClick={() => complete.mutate("repaired")} />
                <ResolveChoice label="Scrapped" onClick={() => complete.mutate("scrapped")} />
                <ResolveChoice label="Sample only" onClick={() => complete.mutate("sample_only")} />
              </div>
            ) : null}
          </div>
          <Button type="button" variant="secondary" disabled={complete.isPending} onClick={() => complete.mutate("escalate")}>
            Escalate to tier 2
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ResolveChoice({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="block w-full rounded px-2 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-100" onClick={onClick}>
      {label}
    </button>
  );
}

function Tier2Form({ incidentId, data }: { incidentId: string; data: ReviewPayload }) {
  const queryClient = useQueryClient();
  const [tried, setTried] = useState("");
  const [result, setResult] = useState("");
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["quality", "tier-review", incidentId] });
  const log = useMutation({
    mutationFn: () =>
      apiFetch(`/api/quality/tier-reviews/${incidentId}/tier-2/log`, {
        method: "POST",
        body: JSON.stringify({ tried, result }),
      }),
    onSuccess: () => {
      setTried("");
      setResult("");
      refresh();
    },
  });
  const loan = useMutation({
    mutationFn: (action: "send" | "return") =>
      apiFetch(`/api/quality/tier-reviews/${incidentId}/tier-2/loan`, {
        method: "POST",
        body: JSON.stringify({ action }),
      }),
    onSuccess: refresh,
  });
  const complete = useMutation({
    mutationFn: (outcome: string) =>
      apiFetch(`/api/quality/tier-reviews/${incidentId}/complete`, {
        method: "POST",
        body: JSON.stringify({ tier: 2, outcome }),
      }),
    onSuccess: refresh,
  });
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>What was tried</CardHeader>
        <CardBody className="space-y-2">
          {data.tier2.log.map((entry) => (
            <p key={entry.id} className="text-sm text-slate-800">{entry.tried}: {entry.result}</p>
          ))}
          {data.tier2.completed ? null : (
            <>
              <textarea className="w-full rounded border border-slate-300 px-2 py-1 text-sm" rows={2} placeholder="What was tried" value={tried} onChange={(event) => setTried(event.target.value)} />
              <textarea className="w-full rounded border border-slate-300 px-2 py-1 text-sm" rows={2} placeholder="Result" value={result} onChange={(event) => setResult(event.target.value)} />
              <Button type="button" variant="secondary" disabled={log.isPending} onClick={() => log.mutate()}>Add note</Button>
            </>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader>Device loan</CardHeader>
        <CardBody className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" disabled={loan.isPending || data.tier2.loanSentAt != null} onClick={() => loan.mutate("send")}>Loan out</Button>
          <Button type="button" variant="secondary" disabled={loan.isPending || data.tier2.loanSentAt == null || data.tier2.loanReturnedAt != null} onClick={() => loan.mutate("return")}>Loan back</Button>
        </CardBody>
      </Card>
      {data.tier2.completed ? <p className="text-sm">Tier 2 is complete ({data.tier2.outcome}).</p> : (
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={complete.isPending} onClick={() => complete.mutate("repaired")}>Repaired</Button>
          <Button type="button" disabled={complete.isPending} onClick={() => complete.mutate("scrapped")}>Scrapped</Button>
          <Button type="button" disabled={complete.isPending} onClick={() => complete.mutate("sample_only")}>Sample only</Button>
        </div>
      )}
    </div>
  );
}
